import type { InlineInputProps } from '@ztechapps/webui.input'
import type { ComponentType, ReactNode } from 'react'
import { Icon } from '@ztechapps/webui.util'
import { AddressEditField } from '@ztechapps/webui.location'
import { humanize } from '@ztechapps/common.utils'
import type { SetOptional, SetRequired } from 'type-fest'
import { VendorAddress } from '@ztechapps/vendor.webui'
import { orderUpdatePurchaseOrderAddressQuery } from '@ztechapps/purchase-order.common'
import { CustomerAddress } from '@ztechapps/customer.webui'

export type InputProps = {
  userId: string
  purchaseOrderId: string
  companyId: string
}

export type PurchaseOrderAddressInlineInputProps = SetOptional<
  SetRequired<InlineInputProps<InputProps>, 'inputProps' | 'parentInputComponent'>,
  'name'
> & { disabledReason?: string }

// Both the vendor and ship-to wrappers hold identical mutation and filter shapes — only the
// picker component differs — so this shared base takes `addressSelectComponent` as its one
// per-surface value.
type PurchaseOrderAddressInlineInputBaseProps = PurchaseOrderAddressInlineInputProps & {
  addressSelectComponent: ComponentType<any>
}

const PurchaseOrderAddressInlineInput = ({
  mutationConfig,
  emptyContent,
  name,
  label,
  initialValues,
  inputProps,
  addressSelectComponent,
  canEdit,
  disabledReason,
  parentInputComponent,
}: PurchaseOrderAddressInlineInputBaseProps) => {
  emptyContent = emptyContent ?? (
    <>
      <Icon name="map-marker" /> <small>{`[Add address]`}</small>
    </>
  )

  label = label ?? `${humanize(name)}`

  // `canEdit` is a straight pass-through: `PurchaseOrderAddress.tsx` computes and supplies it
  // directly from `usePurchaseOrderItemContext()`'s `hasRoleAndNotReceived` — the same role,
  // document-status and lock check `Context.tsx:26-38` derives. No fallback here: every real
  // caller supplies `canEdit`, so a wrapper-level re-derivation would be dead code this library
  // also could not reach correctly (this library cannot import that app-level context —
  // `@nx/enforce-module-boundaries` forbids a `scope:domain` webui library depending on the
  // admin app).

  return (
    // Callers always supply `name` ('vendorAddress' | 'shipToAddress'), which uniquely
    // identifies which of the two purchase-order surfaces is open.
    <AddressEditField
      editorKey={name as string}
      name={name as string}
      label={label as string}
      initialValues={initialValues}
      mutationConfig={mutationConfig ?? { query: orderUpdatePurchaseOrderAddressQuery }}
      canEdit={canEdit}
      disabledReason={disabledReason}
      parentInputComponent={parentInputComponent}
      addressSelectComponent={addressSelectComponent}
      buildFilter={(showAllAddress) => {
        if (showAllAddress) return { 'company._id': inputProps?.companyId }
        return { _id: { $in: [inputProps?.userId] } }
      }}
      updateParams={({ _id, updatedValue }) => {
        return {
          input: {
            _id,
            address: {
              ...updatedValue,
            },
            userId: inputProps.userId,
            purchaseOrderId: inputProps.purchaseOrderId,
            type: name,
          },
        }
      }}
      emptyContent={emptyContent as ReactNode}
    />
  )
}

export const PurchaseOrderVendorAddressInlineInput = (args: PurchaseOrderAddressInlineInputProps) => {
  return <PurchaseOrderAddressInlineInput {...args} addressSelectComponent={VendorAddress} />
}

export const PurchaseOrderShipToAddressInlineInput = (args: PurchaseOrderAddressInlineInputProps) => {
  return <PurchaseOrderAddressInlineInput {...args} addressSelectComponent={CustomerAddress} />
}
